import { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@realvisor/ui';
import toast from 'react-hot-toast';
import { useUpdateDocument, type Document } from '@/hooks';

interface EditDocumentModalProps {
  document: Document;
  open: boolean;
  onClose: () => void;
}

export function EditDocumentModal({ document, open, onClose }: EditDocumentModalProps) {
  const updateDocument = useUpdateDocument();
  
  const [title, setTitle] = useState(document.title);
  const [description, setDescription] = useState(document.description || '');

  useEffect(() => {
    setTitle(document.title);
    setDescription(document.description || '');
  }, [document]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Vyplňte název');
      return;
    }

    try {
      await updateDocument.mutateAsync({
        id: document.id,
        data: {
          title: title.trim(),
          description: description.trim() || undefined,
        },
      });

      toast.success('Dokument upraven');
      onClose();
    } catch {
      toast.error('Nepodařilo se upravit dokument');
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Upravit dokument">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Název dokumentu *
          </label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="např. Telefonní skripty"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Popis (volitelné)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Krátký popis dokumentu..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={3}
          />
        </div>

        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>Soubor:</strong> {document.fileName}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Pro změnu souboru smažte tento dokument a nahrajte nový
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Zrušit
          </Button>
          <Button type="submit" disabled={updateDocument.isPending}>
            {updateDocument.isPending ? 'Ukládám...' : 'Uložit'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
